<template>
    <div class="module-time-tracker">
        <loading-indicator v-if="loading" text="Überprüfung auf Kollisionen"/>
        <template v-if="representationWarning.colliding_absences && representationWarning.colliding_absences.length">
            <div class="info">
                <i class="material-icons md-light" aria-hidden="true" role="img">info</i>
                <div>
                    An folgenden Tagen liegen Kollisionen mit den Vertretungsreglungen aufgrund abwesender Mitarbeiter vor:
                    <ul>
                        <li v-for="absence of representationWarning.colliding_absences">{{absence}}</li>
                    </ul>
                </div>
            </div>
        </template>
        <template v-if="representationWarning.invalid_regulations && representationWarning.invalid_regulations.length">
            <div class="info">
                <i class="material-icons md-light" aria-hidden="true" role="img">info</i>
                <div>
                    Die Anzahl benötigter Mitarbeiter gleicht oder übersteigt die der Mitarbeiter in der Vertretungsregelung! Bitte überprüfen Sie die folgenden Vertretungsregelungen:
                    <ul>
                        <li v-for="regulation of representationWarning.invalid_regulations">{{regulation.title}}</li>
                    </ul>
                </div>
            </div>
        </template>
        <template v-if="representationWarning.colliding_regulations && representationWarning.colliding_regulations.length">
            <template v-for="regulation of representationWarning.colliding_regulations">
                <h2>{{regulation.title}}</h2>
                <absence-calendar :year="currentYear" :regulation-id="regulation.id" :absence-id="entry.id" :display-navigation="false" tag="div"/>
            </template>
        </template>
        <template v-if="representationWarning.colliding_own_absences && representationWarning.colliding_own_absences.length">
            <div class="info">
                <i class="material-icons md-light" aria-hidden="true" role="img">info</i>
                <div>
                    An folgenden Tagen liegen Kollisionen mit eigenen Abwesenheiten vor:
                    <ul>
                        <li v-for="absence of representationWarning.colliding_own_absences">{{absence}}</li>
                    </ul>
                </div>
            </div>
        </template>

        <form-row label="Mitarbeiter" class="margin-top-10">
            <div class="time-tracker-reason-box-bold">{{getUsername(employeeId, 'employee')}}</div>
        </form-row>

        <form-row v-if="entry.request_reason" label="Anfragegrund">
            <div class="time-tracker-reason-box-bold">{{entry.request_reason}}</div>
        </form-row>

        <div class="row">
            <div class="col-sm-6">
                <form-row label="Von">
                    <div class="time-tracker-reason-box-bold">{{startDateTime}}</div>
                </form-row>
            </div>

            <div class="col-sm-6">
                <form-row label="Bis">
                    <div class="time-tracker-reason-box-bold">{{endDateTime}}</div>
                </form-row>
            </div>
        </div>

        <form-row label="Grund">
            <div class="time-tracker-reason-box-bold">{{entry.reason_label || '/'}}</div>
        </form-row>

        <form-row label="Wird von der Wochenarbeitszeit abgezogen">
            <div class="time-tracker-reason-box-bold">{{entry.work_time_substract ? 'Ja' : 'Nein'}}</div>
        </form-row>

        <form-row v-if="entry.request_reason" label="Status">
            <div v-if="0 === entry.state" class="time-tracker-reason-box-bold"><i class="material-icons" aria-hidden="true" role="img">hourglass_empty</i> <span>Beantragt</span>
            </div>
            <div v-else-if="1 === entry.state" class="time-tracker-reason-box-bold"><i class="material-icons text-success" aria-hidden="true" role="img">check</i>
                <span>Antrag angenommen</span></div>
            <div v-else-if="2 === entry.state" class="time-tracker-reason-box-bold"><i class="material-icons text-danger" aria-hidden="true" role="img">clear</i>
                <span>Antrag abgelehnt</span></div>
            <div v-else-if="3 === entry.state" class="time-tracker-reason-box-bold"><i class="material-icons text-warning" aria-hidden="true" role="img">undo</i>
                <span>Zurückziehung beantragt</span></div>
            <div v-else-if="4 === entry.state" class="time-tracker-reason-box-bold"><i class="material-icons text-warning" aria-hidden="true" role="img">hourglass_empty</i>
                <span>Zurückziehung abgelehnt</span></div>
            <div v-else-if="5 === entry.state" class="time-tracker-reason-box-bold"><i class="material-icons text-warning" aria-hidden="true" role="img">hourglass_empty</i>
                <span>Antrag Zurückgezogen</span></div>
        </form-row>

        <form-row v-if="entry.creator" label="Erstellt" class="margin-top-10">
            <div class="time-tracker-reason-box-bold">{{dateHelper('d.m.Y H:i:s', entry.created)}} Uhr von {{getUsername(entry.creator)}}</div>
        </form-row>

        <form-row v-if="entry.editor" label="Bearbeitet">
            <div class="time-tracker-reason-box-bold">{{dateHelper('d.m.Y H:i:s', entry.edited)}} Uhr von {{getUsername(entry.editor)}}</div>
        </form-row>
    </div>
</template>

<script setup lang="ts">
    import FormRow from '@/components/form/FormRow.vue';
    import LoadingIndicator from '@/components/LoadingIndicator.vue';
    import {usePortuna} from '@/composables/portuna';
    import {errorHandler} from '@/utils/portuna';
    import AbsenceCalendar from '@/views/absences/AbsenceCalendar.vue';
    import axios from 'axios';
    import type {TinyEmitter} from 'tiny-emitter';
    import {computed, inject, ref} from 'vue';
    import dateHelper from '../../utils/portuna/dateHelper';

    const portuna = usePortuna();

    const popupEmitter = inject<TinyEmitter>('popup-emitter')!;

    const props = defineProps<{
        id: number;
        employeeId: number;
    }>();

    const currentYear: number   = <number>dateHelper('Y');
    const entry                 = ref<IAbsencesListEntry>({});
    const representationWarning = ref<VacationRegulationWarning>({});
    const loading               = ref<boolean>(false);
    const startDateTime         = computed(() => getTimeValue(entry.value.startTime));
    const endDateTime           = computed(() => getTimeValue(entry.value.endTime));

    const getTimeValue = (time: string) =>
    {
        const dateTime = <string>dateHelper('D, d.m.Y H:i', time);

        if (dateTime.endsWith('00:00'))
        {
            return <string>dateHelper('D, d.m.Y', time);
        }

        return dateTime + ' Uhr';
    };

    const created = () =>
    {
        fetch();
    };

    const fetch = () =>
    {
        loading.value = true;

        axios.get(`/employee/absences/fetch-absence/id/${props.id}/employee/${props.employeeId}`).then(({data: {data}}) =>
        {
            entry.value                 = data.entry;
            representationWarning.value = data.representationWarning;
        })
        .catch(error =>
        {
            errorHandler(error);
            popupEmitter.emit('close');
        })
        .finally(() =>
        {
            loading.value = false;
        });
    };

    created();

    defineExpose(Object.assign({}, portuna));
</script>